import { FormControl, Grid } from '@mui/material';
import React, { useState, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { TypeCard } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/pages/Type/TypeCard';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import {
  PresentationsTabsPath,
  Presentation
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import { useAppSelector } from 'src/utils/Hooks';

export type PresentationType = {
  id: string;
  label: string;
  description: string;
  tags?: string[];
  totalSections?: number;
  totalSlides?: number;
  isFromAPI?: boolean;
  status?: 'ACTIVE' | 'INACTIVE' | 'DRAFT' | 'COMING_SOON';
};

const types: PresentationType[] = [
  {
    id: 'FINAL',
    label: 'Market Share Overview',
    description: 'Insights on market share, competitors, and growth trends.',
    tags: ['Sales', 'Market Share', 'Competitors'],
    totalSections: 3,
    totalSlides: 42
  },
  {
    id: 'behavior-analysis',
    label: 'Shopper Behavior Analysis',
    description: 'Insights on shopper trends, preferences, and buying patterns.',
    status: 'COMING_SOON'
  },
  {
    id: 'new-product-deep-dive',
    label: 'New Product Deep Dive',
    description: 'Insights on product features, performance, and market fit.',
    status: 'COMING_SOON'
  },
  {
    id: 'household-penetration',
    label: 'Household Penetration',
    description: 'Insights on brand reach, consumer adoption, and market presence. ',
    status: 'COMING_SOON'
  },
  {
    id: 'pricing-promotions',
    label: 'Pricing & Promotions',
    description: 'Insights on pricing strategies, discounts, and promotional trends.',
    status: 'COMING_SOON'
  }
];

// Helper function to transform API presentations to PresentationType
const transformAPIPresentation = (apiPresentation: Presentation): PresentationType => {
  // Parse comma-separated tags string into array
  const parsedTags = apiPresentation.tags
    ? apiPresentation.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0)
    : [];

  return {
    id: apiPresentation.presentationId,
    label: apiPresentation.displayName || 'Untitled Presentation',
    description: apiPresentation.displayDescription || 'No description available',
    status: apiPresentation.status,
    totalSections: apiPresentation.tabs?.length || 3,
    tags: parsedTags,
    totalSlides: apiPresentation.slides?.length || 0,
    isFromAPI: true
  };
};

export const PresentationsTypePage = () => {
  const {
    type: { control },
    quota,
    isQuotaLoading,
    presentations,
    isPresentationsLoading,
    presentationsError
  } = usePresentationFormContext();

  const [searchQuery, setSearchQuery] = useState('');
  const { beaconNavigator } = useBeaconNavigator();

  const combinedPresentations = useMemo(() => {
    const apiPresentations = presentations.map(transformAPIPresentation);

    const combined = [...types, ...apiPresentations];
    return combined;
  }, [presentations]);

  const filteredTypes = combinedPresentations.filter(
    (type) =>
      type.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isStacklineUser = useAppSelector((state) => state.user.config.isStacklineUser);
  const disabled = isQuotaLoading || (!isStacklineUser && quota && quota.usage >= quota.limit);

  return (
    <Flex flexDirection="column" gap={32}>
      <Flex flexDirection="column">
        <Text variant="h4" lineHeight="28px">
          Presentation Type
        </Text>
        <Flex marginTop="8px" fontSize={16} lineHeight="24px">
          Choose the type of presentation that you would like to build.
        </Flex>
        {/* Display presentations loading state and data info */}
        {isPresentationsLoading && (
          <Flex marginTop="4px">
            <Text variant="body2" opacity={70}>
              Loading presentations data...
            </Text>
          </Flex>
        )}
        {presentationsError && (
          <Flex marginTop="4px">
            <Text variant="body2">Error loading presentations: {presentationsError.message}</Text>
          </Flex>
        )}
      </Flex>
      <Flex flexDirection="column" gap={32}>
        <SearchTextField
          placeholder="Search presentations"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Controller
          control={control}
          name="reportType"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error}>
              <Grid container spacing={2}>
                {filteredTypes.map((type) => (
                  <Grid item xs={4} key={type.id}>
                    <TypeCard
                      disabled={disabled}
                      type={type}
                      isSelected={field.value.id === type.id}
                      onSelect={(type) => {
                        field.onChange(type);
                        beaconNavigator.setPathname(PresentationsTabsPath.selections).go();
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormControl>
          )}
        />
      </Flex>
    </Flex>
  );
};
